import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useNotifications } from '../../hooks/useNotifications'
import {
  Home,
  MessageSquare,
  User,
  FileText,
  Package,
  Search,
  Building2,
  Reply,
  Settings,
  LayoutDashboard,
  LucideIcon,
  X
} from 'lucide-react'

interface MobileFooterProps {
  // Optional override for message count (for backward compatibility)
  messageNotificationCount?: number
}

interface NavItem {
  label: string
  icon: LucideIcon
  path: string
  key: string
  badge?: number
}

export default function MobileFooter({ messageNotificationCount }: MobileFooterProps) {
  // Initialize mobile state with proper detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [conversations, setConversations] = useState<any[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const { user, isAuthenticated } = useAuthStore()
  const { notificationCounts } = useNotifications()
  const location = useLocation()
  const navigate = useNavigate()

  // Use prop override if provided, otherwise use hook data
  const actualMessageCount = messageNotificationCount !== undefined
    ? messageNotificationCount
    : notificationCounts.messageUnread

  // Fetch conversations for message modal
  const fetchConversations = async () => {
    if (!user) return
    
    setLoadingMessages(true)
    try {
      const response = await fetch('/api/messages/conversations')
      const data = await response.json()
      if (data.success) {
        setConversations(data.data || [])
      }
    } catch (error) {
      console.error('Error fetching conversations:', error)
    } finally {
      setLoadingMessages(false)
    }
  }

  // Handle message item click
  const handleMessageClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowMessageModal(true)
    fetchConversations()
  }

  // Check if we're on mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }

    // Only add listener if not already the correct state
    if (window.innerWidth < 768 !== isMobile) {
      checkMobile()
    }

    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [isMobile])

  // Don't render if not mobile or not authenticated
  if (!isMobile || !isAuthenticated) {
    return null
  }

  // Get user type for dynamic navigation
  const getUserType = () => {
    if (!user) return 'BUYER'

    // Special handling for admin users
    if (user.role === 'ADMIN') {
      return 'ADMIN'
    }

    if (user.role === 'ENTERPRISE_VENDOR' || (user.role === 'VENDOR' && user.isEnterprise)) {
      return 'ENTERPRISE_VENDOR'
    }
    if (user.role === 'ENTERPRISE_BUYER' || (user.role === 'BUYER' && user.isEnterprise)) {
      return 'ENTERPRISE_BUYER'
    }
    if (user.role === 'VENDOR') {
      return 'VENDOR'
    }
    return 'BUYER'
  }

  const userType = getUserType()

  // Get dynamic navigation item based on user role
  const getDynamicNavItem = (): NavItem => {
    switch (userType) {
      case 'ADMIN':
        return {
          label: 'Settings',
          icon: Settings,
          path: '/admin/settings',
          key: 'settings'
        }
      case 'ENTERPRISE_VENDOR':
        return {
          label: 'RFQs',
          icon: FileText,
          path: '/enterprise/vendor-rfqs',
          key: 'rfqs'
        }
      case 'ENTERPRISE_BUYER':
        return {
          label: 'MY RFQS',
          icon: FileText,
          path: '/enterprise/rfqs',
          key: 'rfqs'
        }
      case 'VENDOR':
        return {
          label: 'My Sales',
          icon: Package,
          path: '/products/my',
          key: 'sales'
        }
      case 'BUYER':
      default:
        return {
          label: 'Products',
          icon: Search,
          path: '/products',
          key: 'products'
        }
    }
  }

  const dynamicNavItem = getDynamicNavItem()

  // Always define Home and Profile as fixed items
  const homeItem: NavItem = {
    label: 'Home',
    icon: Home,
    path: '/',
    key: 'home'
  }

  const profileItem: NavItem = {
    label: 'Profile',
    icon: User,
    path: '/profile',
    key: 'profile'
  }

  const messagesItem: NavItem = {
    label: 'Messages',
    icon: MessageSquare,
    path: '#', // Use # to prevent navigation
    key: 'messages',
    badge: actualMessageCount > 0 ? actualMessageCount : undefined
  }

  const dashboardItem: NavItem = {
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/admin',
    key: 'dashboard'
  }

  // Build middle items based on user type
  let middleItems: NavItem[] = []

  if (userType === 'ADMIN') {
    middleItems = [
      dynamicNavItem, // Settings
      dashboardItem  // Dashboard
    ]
  } else if (userType === 'ENTERPRISE_VENDOR') {
    middleItems = [
      dynamicNavItem, // RFQs
      {
        label: 'Responses',
        icon: Reply,
        path: '/quotes/vendor',
        key: 'responses'
      },
      messagesItem
    ]
  } else if (userType === 'ENTERPRISE_BUYER') {
    middleItems = [
      dynamicNavItem, // MY RFQs
      messagesItem,
      {
        label: 'Vendors',
        icon: Building2,
        path: '/enterprise',
        key: 'vendors'
      }
    ]
  } else {
    // Regular users (BUYER, VENDOR)
    middleItems = [
      dynamicNavItem, // Products or My Sales
      messagesItem
    ]
  }

  // Always construct navItems with Home first and Profile last
  const navItems: NavItem[] = [homeItem, ...middleItems, profileItem]

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  // Check if we're on the home page
  const isOnHomePage = location.pathname === '/'

  return (
    <nav className={`fixed bottom-0 left-0 right-0 border-t z-50 md:hidden ${
      isOnHomePage
        ? 'bg-transparent border-transparent shadow-none'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg'
    }`}>
      <div className="flex items-center justify-around h-16 w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
        {navItems.map((item) => {
          const IconComponent = item.icon
          const active = isActive(item.path)
          const isMessageItem = item.key === 'messages'

          const itemContent = (
            <div className={`flex flex-col items-center justify-center flex-1 py-2 px-1 min-w-0 relative ${
              active
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}>
              <div className="relative">
                <IconComponent
                  size={20}
                  className={`mb-1 ${
                    active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                  }`}
                />
                {item.badge && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span className={`text-xs font-medium ${
                active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
              }`}>
                {item.label}
              </span>
            </div>
          )

          if (isMessageItem) {
            return (
              <button
                key={item.key}
                onClick={handleMessageClick}
                className="flex-1 min-w-0"
              >
                {itemContent}
              </button>
            )
          }

          return (
            <Link
              key={item.key}
              to={item.path}
              className="flex-1 min-w-0"
            >
              {itemContent}
            </Link>
          )
        })}
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white dark:bg-gray-800 w-full max-h-[80vh] rounded-t-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Messages</h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {loadingMessages ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Loading messages...</p>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No messages yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      onClick={() => {
                        setShowMessageModal(false)
                        navigate(`/messages?user=${conversation.otherUser?.id || conversation.otherParticipant?.id}`)
                      }}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            {(conversation.otherUser?.firstName?.[0] || conversation.otherParticipant?.firstName?.[0] || 'U').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {conversation.otherUser?.firstName || conversation.otherParticipant?.firstName} {conversation.otherUser?.lastName || conversation.otherParticipant?.lastName}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {conversation.lastMessage?.content || 'No messages yet'}
                          </p>
                        </div>
                        {conversation.unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                            {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowMessageModal(false)
                  navigate('/messages')
                }}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                View All Messages
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}